import * as React from "react";
import { View } from "react-native";
import * as ImagePicker from "expo-image-picker";
import Cropper, { Area, CropperProps } from "react-easy-crop";

import { cn } from "~/lib/utils";
import { Button } from "~/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "~/ui/Dialog";
import { AccountAvatar } from "../AccountAvatar";

export { Area } from "react-easy-crop";

interface ImageCropperProps {
  image: string;
  aspect?: number;
  className?: string;
  onCropped: (area: Area) => void;
}

export function ImageCropper(props: ImageCropperProps) {
  const { className, image, aspect = 1, onCropped } = props;

  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);

  return (
    <View className={className}>
      <Cropper
        image={image}
        crop={crop}
        zoom={zoom}
        onCropChange={setCrop}
        onZoomChange={setZoom}
        aspect={aspect}
        showGrid={false}
        cropShape="round"
        onCropComplete={(_, croppedAreaPixels) => onCropped(croppedAreaPixels)}
        style={{
          containerStyle: {
            backgroundColor: "rgba(64, 64, 64)",
            borderRadius: "8px",
          },
          mediaStyle: {
            maxWidth: "unset",
          },
        }}
        maxZoom={10}
        objectFit="cover"
      />
    </View>
  );
}

export async function cropImage(image: string, area: Area) {
  return new Promise<string>((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = area.width;
    canvas.height = area.height;
    const imageObj = new Image();
    imageObj.src = image;
    imageObj.onload = () => {
      ctx.drawImage(
        imageObj,
        area.x,
        area.y,
        area.width,
        area.height,
        0,
        0,
        area.width,
        area.height,
      );
      const url = canvas.toDataURL("image/jpeg");
      canvas.remove();
      resolve(url);
    };
  });
}

interface AvatarEditorProps {
  avatar?: string;
  fallbackText?: string;
  onChange: (avatar: string) => void;
  readOnly?: boolean;
}

export function AvatarEditor(props: AvatarEditorProps) {
  const { avatar, fallbackText, onChange, readOnly = false } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const [image, setImage] = React.useState<string | undefined>(undefined);

  return (
    <View>
      <View className="flex w-32 flex-col gap-4">
        <AccountAvatar avatar={avatar} fallbackText={fallbackText} size={128} />
        <Button
          onPress={() =>
            pickAvatar().then((image) => {
              if (!image) return;
              else {
                setImage(image);
                setIsOpen(true);
              }
            })
          }
          variant="secondary"
          size="small"
          disabled={readOnly}
        >
          Update Avatar
        </Button>
      </View>
      {isOpen && image && (
        <AvatarCropper
          image={image}
          onCancel={() => {
            setIsOpen(false);
            setImage(undefined);
          }}
          onSave={(dataUri) => {
            setImage(undefined);
            setIsOpen(false);
            onChange(dataUri);
          }}
        />
      )}
    </View>
  );
}

export function AvatarCropper(props: {
  image: string;
  onCancel: () => void;
  onSave: (image: string) => void;
}) {
  const { image, onCancel, onSave } = props;
  const croppedRef = React.useRef<Area | null>(null);
  return (
    <Dialog open={true} onOpenChange={() => onCancel()}>
      <DialogContent className="w-72">
        <DialogTitle>Move and scale</DialogTitle>
        <DialogDescription>
          Drag to move, scroll or pinch to zoom.
        </DialogDescription>
        <ImageCropper
          image={image}
          aspect={1}
          onCropped={(area) => {
            croppedRef.current = area;
          }}
          className="size-60"
        />
        <DialogFooter>
          <Button onPress={onCancel} variant="outline">
            Cancel
          </Button>
          <Button
            onPress={() => {
              if (croppedRef.current)
                cropImage(image, croppedRef.current).then((result) => {
                  onSave(result);
                });
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export async function pickAvatar() {
  await ImagePicker.requestMediaLibraryPermissionsAsync();
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    allowsEditing: true,
    allowsMultipleSelection: false,
    presentationStyle: ImagePicker.UIImagePickerPresentationStyle.FORM_SHEET,
    base64: true,
    aspect: [1, 1],
  });
  if (result.canceled) return null;
  const asset = result.assets[0];
  return asset.uri;
}
